import * as React from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { t } from '~i18n';
import { ColumnDefinition, ColumnState } from './useColumnManager';
import styles from './ColumnManager.module.css';

interface Props {
  columns: ColumnState[];
  allColumns: ColumnDefinition[];
  isDirty: boolean;
  onChange: (next: ColumnState[]) => void;
  onToggle: (id: string) => void;
  onSetAll: (visible: boolean) => void;
  onReset: () => void;
  onClose: () => void;
}

interface RowProps {
  id: string;
  label: string;
  visible: boolean;
  onToggle: () => void;
  draggable: boolean;
}

const Row: React.FC<RowProps> = ({ id, label, visible, onToggle, draggable }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !draggable,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.Row}>
      <span className={styles.Handle} {...attributes} {...listeners} aria-label={t('columnManager.dragHint')}>
        <GripVertical size={14} />
      </span>
      <input type="checkbox" className={styles.Check} id={`cm-${id}`} checked={visible} onChange={onToggle} />
      <label htmlFor={`cm-${id}`} className={styles.Label}>
        {label}
      </label>
    </div>
  );
};

export const ColumnManager: React.FC<Props> = ({
  columns,
  allColumns,
  isDirty,
  onChange,
  onToggle,
  onSetAll,
  onReset,
  onClose,
}) => {
  const [query, setQuery] = React.useState('');
  const popRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current && popRef.current.contains(target)) return;
      const toggle = document.querySelector('[data-cm-toggle]');
      if (toggle && toggle.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const labelById = React.useMemo(() => new Map(allColumns.map((d) => [d.id, d.label])), [allColumns]);

  const trimmedQuery = query.trim().toLowerCase();
  const filtered = trimmedQuery
    ? columns.filter((c) => (labelById.get(c.id) ?? '').toLowerCase().includes(trimmedQuery))
    : columns;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = columns.findIndex((c) => c.id === active.id);
    const to = columns.findIndex((c) => c.id === over.id);
    if (from < 0 || to < 0) return;
    onChange(arrayMove(columns, from, to));
  };

  const dragEnabled = !trimmedQuery;

  return (
    <div className={styles.Pop} ref={popRef} role="dialog" aria-label={t('columnManager.title')}>
      <div className={styles.Head}>
        <div className={styles.TitleRow}>
          <div className={styles.Title}>{t('columnManager.title')}</div>
        </div>
        <div className={styles.Search}>
          <i className={`icon-search ${styles.SearchIcon}`} />
          <input
            type="text"
            placeholder={t('columnManager.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
      </div>

      <div className={styles.Toolbar}>
        <button className={styles.Link} type="button" onClick={() => onSetAll(true)}>
          {t('columnManager.showAll')}
        </button>
        <span className={styles.Divider} />
        <button className={styles.Link} type="button" onClick={() => onSetAll(false)}>
          {t('columnManager.hideAll')}
        </button>
        <div className={styles.Spacer} />
        <button className={styles.Link} type="button" onClick={onReset} disabled={!isDirty}>
          {t('columnManager.reset')}
        </button>
      </div>

      <div className={styles.List}>
        {filtered.length === 0 && <div className={styles.Empty}>{t('columnManager.empty', { query: query })}</div>}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={filtered.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            {filtered.map((col) => (
              <Row
                key={col.id}
                id={col.id}
                label={labelById.get(col.id) ?? col.id}
                visible={col.visible}
                onToggle={() => onToggle(col.id)}
                draggable={dragEnabled}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <div className={styles.Foot}>
        <span className={styles.FootHint}>
          {trimmedQuery ? t('columnManager.searchHint') : t('columnManager.dragHint')}
        </span>
        <button className={styles.Link} type="button" onClick={onClose}>
          {t('columnManager.done')}
        </button>
      </div>
    </div>
  );
};
